<template>
  <div class="main-content">

 
  <div class="userlogin-home">
    <section class="homepg-sec userlogin-home-hero">
      <div class="container">
        <div class="home-headLogo">
          <img src="/images/logo.png" alt="logo" />
          <p>The only resource you need for MLA</p>
        </div>
      </div>
    </section>

    <section class="homepg-card-sec userlogin-home-cards">
      <div class="container">
        <div class="questions-notes-mocks userlogin-progress-cards">
          <div
            v-for="(card, index) in cards"
            :key="card.key"
            class="mla-question-box"
            :class="{
              'mla-note-box': card.key === 'notes',
              'mla-mock-box': card.key === 'mocks',
              'is-section-locked': isSectionLocked(card.key),
            }"
            data-aos="fade-up"
            :data-aos-delay="index * 100"
            data-aos-duration="800"
            @click="gotoroute(card.route, card.key)"
          >
            <SectionLockBadge v-if="isSectionLocked(card.key)" />
            <div class="section-card-body">
              <h4>
                <img :src="card.image" :alt="card.title" />
                {{ card.title }}
              </h4>
              <ProgressBar
                v-if="!isSectionLocked(card.key)"
                :progress="progressFor(card.key)"
                label="Progress"
                :detail="progressDetailFor(card.key)"
              />
            </div>
            <div class="cardbottom-shadow">
              <img src="/images/cardshadow.png" alt="" />
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>

  <SectionSubscribeModal
    :open="showSubscribePopup"
    :message="subscribeMessage"
    @close="showSubscribePopup = false"
  />
  </div>
</template>

<script>
import ProgressBar from '@/components/HomepageProgress.vue';
import SectionLockBadge from '@/components/SectionLockBadge.vue';
import SectionSubscribeModal from '@/components/SectionSubscribeModal.vue';
import { get } from './lib/api';
import { hydrateNoteUserState, notesProgressStats, rebuildNotesNumberRegistry, invalidateNotesPrefsCache } from './lib/notesUserPrefs';
import {
  mocksAggregateProgressPercent,
  mocksAggregateTotals,
} from './lib/mocksAggregate';
import { emptySectionLocks, fetchSectionLocks, subscribeSectionMessage } from './lib/sectionLocks';

export default {
  name: 'UserLogin',
  components: {
    ProgressBar,
    SectionLockBadge,
    SectionSubscribeModal,
  },
  data() {
    return {
      clinicalAreas: [],
      professionalKnowledge: [],
      notesClinicalAreas: [],
      notesProfessionalKnowledge: [],
      mocksData: [],
      notesReadRev: 0,
      sectionLocks: emptySectionLocks(),
      showSubscribePopup: false,
      subscribeMessage: '',
      cards: [
        {
          key: 'questions',
          title: 'Questions',
          image: '/images/questionmark.png',
          route: '/mlalistselection',
        },
        {
          key: 'notes',
          title: 'Notes',
          image: '/images/file.png',
          route: '/noteslistselection',
        },
        {
          key: 'mocks',
          title: 'Mocks',
          image: '/images/brain.png',
          route: '/mocksection',
        },
      ],
    };
  },
  computed: {
    questionsProgressPercent() {
      const total = this.questionsTotal;
      if (!total) return 0;
      return Math.round((this.questionsAttempted / total) * 100);
    },
    questionsAttempted() {
      const all = [...this.clinicalAreas, ...this.professionalKnowledge];
      return all.reduce((s, i) => s + (parseInt(i.attempted_count, 10) || 0), 0);
    },
    questionsTotal() {
      const all = [...this.clinicalAreas, ...this.professionalKnowledge];
      return all.reduce((s, i) => s + (parseInt(i.questions_count, 10) || 0), 0);
    },
    notesSidebarProgress() {
      void this.notesReadRev;
      void this.notesClinicalAreas.length;
      void this.notesProfessionalKnowledge.length;
      return notesProgressStats(this.$store, [
        ...this.notesClinicalAreas,
        ...this.notesProfessionalKnowledge,
      ]);
    },
    notesProgressPercent() {
      return this.notesSidebarProgress.percent;
    },
    notesRead() {
      return this.notesSidebarProgress.read;
    },
    notesTotal() {
      return this.notesSidebarProgress.total;
    },
    mocksProgressPercent() {
      return mocksAggregateProgressPercent(this.mocksData);
    },
    mocksAttempted() {
      return mocksAggregateTotals(this.mocksData).totalAttempted;
    },
    mocksTotalQuestions() {
      return mocksAggregateTotals(this.mocksData).totalQuestions;
    },
  },
  created() {
    hydrateNoteUserState();

    fetchSectionLocks(true).then((locks) => {
      this.sectionLocks = locks;
    });

    get('/getsubjectclient').then((res) => {
      this.clinicalAreas = res.data.clinical || [];
      this.professionalKnowledge = res.data.professional || [];
    });

    get('/notesclient').then((res) => {
      if (res.data.chaptersClinical) {
        this.notesClinicalAreas = this.transformChaptersData(res.data.chaptersClinical);
      }
      if (res.data.chaptersProfessioanl) {
        this.notesProfessionalKnowledge = this.transformChaptersData(res.data.chaptersProfessioanl);
      }
      rebuildNotesNumberRegistry([
        { items: this.notesClinicalAreas, listMode: 'chapter' },
        { items: this.notesProfessionalKnowledge, listMode: 'chapter' },
      ]);
    }).catch(() => { /* ignore */ });

    get('/getmocks').then((res) => {
      this.mocksData = res.data.data || [];
    });
  },
  mounted() {
    this._notesPrefsStorage = (ev) => {
      if (ev && ev.key && String(ev.key).indexOf('notesUserPrefs_') === 0) {
        invalidateNotesPrefsCache();
        this.notesReadRev += 1;
      }
    };
    window.addEventListener('storage', this._notesPrefsStorage);
  },
  activated() {
    this.notesReadRev += 1;
    fetchSectionLocks(true).then((locks) => {
      this.sectionLocks = locks;
    });
  },
  beforeDestroy() {
    if (this._notesPrefsStorage) {
      window.removeEventListener('storage', this._notesPrefsStorage);
    }
  },
  methods: {
    isSectionLocked(key) {
      return !!(this.sectionLocks && this.sectionLocks[key]);
    },
    openSubscribePopup(sectionKey) {
      this.subscribeMessage = subscribeSectionMessage(sectionKey);
      this.showSubscribePopup = true;
    },
    gotoroute(path, key) {
      if (key && this.isSectionLocked(key)) {
        this.openSubscribePopup(key);
        return;
      }
      this.$router.push(path);
    },
    progressFor(key) {
      if (key === 'questions') return this.questionsProgressPercent;
      if (key === 'notes') return this.notesProgressPercent;
      if (key === 'mocks') return this.mocksProgressPercent;
      return 0;
    },
    progressDetailFor(key) {
      if (key === 'questions') return `${this.questionsAttempted}/${this.questionsTotal}`;
      if (key === 'notes') return `${this.notesRead}/${this.notesTotal}`;
      if (key === 'mocks') return `${this.mocksAttempted}/${this.mocksTotalQuestions}`;
      return '';
    },
    transformChaptersData(chapters) {
      if (!chapters || !Array.isArray(chapters)) return [];

      return chapters.map((chapter) => {
        const transformedChapter = {
          id: chapter.id || null,
          title: chapter.title || '',
          index_number: chapter.index_number || null,
        };

        if (chapter.notes && Array.isArray(chapter.notes) && chapter.notes.length > 0) {
          transformedChapter.children = chapter.notes.map((note) => {
            const transformedNote = {
              id: note.id || null,
              title: note.title || '',
              index_number: note.index_number || null,
              number: note.number || null,
            };

            if (note.sub_notes && Array.isArray(note.sub_notes) && note.sub_notes.length > 0) {
              transformedNote.children = note.sub_notes.map((subNote) => ({
                id: subNote.id || null,
                title: subNote.title || '',
                index_number: subNote.index_number || null,
                number: subNote.number || null,
              }));
            }

            return transformedNote;
          });
        }

        return transformedChapter;
      });
    },
  },
};
</script>

<style scoped>

.progress-wrapper {
  font-size: 14px !important;
}

.userlogin-home {
  width: 100%;
  box-sizing: border-box;
  padding-bottom: 80px;
}

.main-content {
  flex: 1;
  height: 85vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.userlogin-home-hero {
  padding: 40px 0 0;
}

.userlogin-home .home-headLogo {
  margin: 0 0 48px;
  text-align: center;
}

.userlogin-home .home-headLogo img {
  width: min(250px, 58vw);
  max-width: 100%;
  height: auto;
  padding: 0 0 16px;
}

.userlogin-home .home-headLogo p {
  margin: 0;
  padding: 0 16px;
  font-size: clamp(13px, 2.4vw, 16px);
}

.userlogin-home-cards {
  overflow: visible;
  padding: 12px 0 40px !important;
}

.userlogin-home-cards .container {
  width: 100%;
  max-width: 1100px;
  padding-left: 16px;
  padding-right: 16px;
  box-sizing: border-box;
}

.userlogin-progress-cards {
  display: flex;
  flex-wrap: nowrap;
  justify-content: center;
  align-items: stretch;
  gap: 28px;
  width: 100%;
  margin: 0 auto;
  box-sizing: border-box;
}

.userlogin-progress-cards .mla-question-box {
  width: 100%;
  max-width: 280px;
  flex: 1 1 0;
  min-width: 0;
  margin: 0;
  padding: 0;
  cursor: pointer;
  transition: transform 0.25s ease;
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden;
}

.userlogin-progress-cards .mla-question-box .section-card-body {
  flex: 1;
  min-width: 0;
  padding: 5px 6px 2px;
  box-sizing: border-box;
}

.userlogin-progress-cards .mla-question-box.is-section-locked {
  cursor: not-allowed;
}

.userlogin-progress-cards .mla-question-box.is-section-locked:hover {
  transform: none;
}

/* Locked: keep same card size; center title where progress was */
.userlogin-progress-cards .mla-question-box.is-section-locked .section-card-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.userlogin-progress-cards .mla-question-box.is-section-locked h4 {
  padding: 0;
}

.userlogin-progress-cards .mla-question-box:hover {
  transform: translateY(-4px);
}

.mla-question-box {
  background: #1a90ff;
  border: 1px solid #0276ba;
  padding: 5px 6px 2px 6px;
  border-radius: 16px;
  position: relative;
  text-align: center;
  box-sizing: border-box;
  border: none;
}

.mla-question-box .cardbottom-shadow img {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  bottom: -27px;
  width: 350px;
  height: auto;
  z-index: -1;
  pointer-events: none;
}

.mla-question-box h4 {
  margin: 0;
  font-size: 22px;
  color: #fff;
  text-transform: uppercase;
  padding: 0 0 6px 0;
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
  font-family: Helvetica, sans-serif;
}

.mla-question-box h4 img {
  width: 27px;
  height: 27px;
  object-fit: contain;
  flex-shrink: 0;
}

.mla-note-box {
  background-color: #f59c00;
  border-color: #f59c00;
  border: none;
}

.mla-mock-box {
  background-color: #ed1c24;
  border-color: #ed1c24;
  border: none;
}

@media (max-width: 991px) {
  .userlogin-home .home-headLogo {
    margin-bottom: 36px;
  }

  .userlogin-progress-cards {
    gap: 18px;
  }

  .userlogin-progress-cards .mla-question-box {
    max-width: 240px;
  }

  .mla-question-box h4 {
    font-size: 18px;
  }
}

@media (max-width: 767px) {
  .userlogin-home {
    padding-bottom: 56px;
  }

  .userlogin-home-hero {
    padding-top: 24px;
  }

  .userlogin-home .home-headLogo {
    margin-bottom: 28px;
  }

  .userlogin-home .home-headLogo img {
    width: min(200px, 52vw);
    padding-bottom: 12px;
  }

  .userlogin-progress-cards {
    flex-direction: column;
    flex-wrap: wrap;
    align-items: center;
    gap: 40px;
  }

  .userlogin-progress-cards .mla-question-box {
    width: 320px;
    max-width: 100%;
    flex: 0 0 auto;
  }
}

@media (max-width: 400px) {
  .userlogin-home-cards .container {
    padding-left: 12px;
    padding-right: 12px;
  }

  .userlogin-progress-cards {
    gap: 34px;
  }

  .userlogin-progress-cards .mla-question-box {
    width: 100%;
    max-width: 320px;
    flex: 0 0 auto;
  }
}
</style>
